import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import EmptyState from '@/components/feature/EmptyState';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { allArticles, getArticlesByStar, getStarById } from '@/utils/articles';
import { formatViews } from '@/utils/format';

export default function StarDetailPage() {
  const { id = '' } = useParams();
  const star = getStarById(id);

  useDocumentMeta(
    star ? `${star.name} 明星专题 - 91黑料` : '明星专题 - 91黑料',
    star ? `91黑料${star.name}明星专题：${star.bio}` : undefined,
  );

  if (!star) {
    return (
      <div className="max-w-content mx-auto px-4 md:px-6 py-10">
        <EmptyState
          icon="ri-emotion-sad-line"
          title="未找到该明星专题"
          description="你访问的明星专题可能已下线，返回明星列表看看其他艺人。"
          actionTo="/star"
          actionLabel="返回明星专题"
        />
      </div>
    );
  }

  const byStar = getArticlesByStar(star.id);
  const byTags = allArticles.filter(
    (a) => a.starId !== star.id && a.tags.some((t) => star.tags.includes(t)),
  );
  const related = (byStar.length > 0 ? byStar : byTags).slice(0, 8);
  const totalViews = byStar.reduce((sum, a) => sum + a.views, 0);

  return (
    <div className="max-w-content mx-auto px-4 md:px-6 py-6 md:py-8">
      <Breadcrumb items={[{ label: '明星专题', to: '/star' }, { label: star.name }]} />

      <section className="mt-5 md:mt-6 bg-background-50 border border-background-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr]">
          <div className="relative w-full h-56 md:h-full bg-background-100">
            <img
              src={star.avatar}
              alt={star.name}
              title={`${star.name}-91黑料明星专题`}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="p-5 md:p-8">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl md:text-3xl font-black text-foreground-950">{star.name}</h1>
              <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                {star.title}
              </span>
              <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium flex items-center gap-1">
                <i className="ri-fire-line"></i> 热度 {star.heat}
              </span>
            </div>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mt-4">{star.bio}</p>
            <div className="flex flex-wrap gap-2 mt-4">
              {star.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs">
                  {tag}
                </span>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3 mt-6 max-w-md">
              <div className="bg-background-100 rounded-lg p-3 text-center">
                <p className="text-lg font-black text-primary-600 font-label">{star.followers}</p>
                <p className="text-xs text-foreground-500 mt-0.5">关注人数</p>
              </div>
              <div className="bg-background-100 rounded-lg p-3 text-center">
                <p className="text-lg font-black text-primary-600 font-label">{byStar.length}</p>
                <p className="text-xs text-foreground-500 mt-0.5">专题内容</p>
              </div>
              <div className="bg-background-100 rounded-lg p-3 text-center">
                <p className="text-lg font-black text-primary-600 font-label">{formatViews(totalViews)}</p>
                <p className="text-xs text-foreground-500 mt-0.5">累计阅读</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mt-8 md:mt-10">
        <div className="flex items-center gap-2.5 mb-5">
          <span className="w-9 h-9 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center">
            <i className="ri-article-line text-xl"></i>
          </span>
          <div>
            <h2 className="text-lg md:text-xl font-black text-foreground-950">相关爆料合集</h2>
            <p className="text-xs md:text-sm text-foreground-500 mt-0.5">
              与 {star.name} 相关的爆料与资讯内容
            </p>
          </div>
        </div>

        {related.length === 0 ? (
          <EmptyState
            icon="ri-file-search-line"
            title="暂无相关爆料"
            description={`目前还没有与 ${star.name} 相关的内容，先去看看其他明星专题吧。`}
            actionTo="/star"
            actionLabel="返回明星专题"
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {related.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        )}
      </section>

      <div className="mt-8 flex justify-center">
        <Link
          to="/star"
          className="px-6 py-2.5 rounded-md bg-background-100 text-foreground-800 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap flex items-center gap-2"
        >
          <i className="ri-arrow-left-line"></i> 返回明星专题
        </Link>
      </div>
    </div>
  );
}